// Shared payslip data builder: turns one { gridRow, payroll, items } into the four boxes
// (รายได้ / รายการหัก / สรุปยอด / ข้อมูลสรุป) used by both the printed payslip
// (printDriverSalaryV3Summary) and the on-screen summary in PayrollDetailDialog.

// หักอื่นๆ — printed last in the รายการหัก box, with its หมายเหตุ (Dscp) beside it in สรุปยอด.
// Branch-specific ExpId: keep it in step with OTH_DEDUCT_EXP_ID in driver-payroll-v3-db.js.
export const OTH_DEDUCT_EXP_ID = 328

// group items by ExpId, summing amounts; label is the expense name
const groupByExp = (items) => {
  const map = new Map()
  for (const it of items) {
    const cur = map.get(it.ExpId) || { ExpId: it.ExpId, label: it.ExpNm || "-", amnt: 0 }
    cur.amnt += +(it.Amnt || 0)
    map.set(it.ExpId, cur)
  }
  return [...map.values()]
}

// Both boxes ALWAYS list every master ExpNm (ExpTypId 20 income / 21 deduction) with its amount
// (0 if none) — like the old DriverSalary payslip — plus, on the income side, the ค่าเที่ยว+อื่นๆ
// trip line. Any booked line whose ExpId isn't in the master is appended so the box total
// still reconciles.
export const buildSlipData = ({ gridRow = {}, payroll = {}, items = [] }, expCols = []) => {
  const income = items.filter(i => i.InOutTyp === "O")
  const deduct = items.filter(i => i.InOutTyp === "I")
  // trip lines (tied to a job order) roll up into one ค่าเที่ยว+อื่นๆ row
  const tripSum = income.filter(i => i.JobOrdId != null).reduce((s, i) => s + +(i.Amnt || 0), 0)

  // amount actually booked per ExpId (non-trip income + every deduction)
  const amntByExp = new Map()
  for (const it of [...income.filter(i => i.JobOrdId == null), ...deduct]) {
    amntByExp.set(it.ExpId, (amntByExp.get(it.ExpId) || 0) + +(it.Amnt || 0))
  }
  const buildRows = (inOut, srcItems) => {
    const cols = expCols.filter(c => c.kind === "exp" && c.InOutTyp === inOut)
    const colIds = new Set(cols.map(c => c.ExpId))
    const rows = cols.map(c => ({ ExpId: c.ExpId, label: c.label, amnt: amntByExp.get(c.ExpId) || 0 }))
    return rows.concat(groupByExp(srcItems.filter(i => !colIds.has(i.ExpId)))) // append off-master lines
  }

  const incomeRows = buildRows("O", income.filter(i => i.JobOrdId == null))
  incomeRows.push({ label: "ค่าเที่ยว+อื่นๆ", amnt: tripSum })
  const deductRows = buildRows("I", deduct)
  // หักอื่นๆ always sits last in the deduction box — the สรุปยอด box prints its หมายเหตุ on the
  // same line, so it must be at a fixed, known position
  const othPos = deductRows.findIndex(r => r.ExpId === OTH_DEDUCT_EXP_ID)
  if (othPos >= 0) deductRows.push(deductRows.splice(othPos, 1)[0])

  const SumInc = income.reduce((s, i) => s + +(i.Amnt || 0), 0)
  const SumDeduct = deduct.reduce((s, i) => s + +(i.Amnt || 0), 0)
  // advance draws (ExpId 78/79) — separate pc_transactions already paid out in cash, so they
  // come off take-home in สรุปยอด. This branch seeds only 79; advAttn stays 0 unless 78 exists.
  const advAttn = +(gridRow.AdvAttnAmnt || 0)
  const advTrip = +(gridRow.AdvTripAmnt || 0)
  // the หักอื่นๆ note(s) — shown next to that row in สรุปยอด
  const OthDeDscp = deduct.filter(i => i.ExpId === OTH_DEDUCT_EXP_ID && i.Dscp).map(i => i.Dscp).join(", ")
  return {
    DrvNm: gridRow.DrvNm, DrvCd: gridRow.DrvCd, SalDte: gridRow.SalDte,
    incomeRows, deductRows, SumInc, SumDeduct, advAttn, advTrip, OthDeDscp,
    Net: SumInc - SumDeduct - advAttn - advTrip,
    NumSts1: gridRow.NumSts1, NumSts3: gridRow.NumSts3,
    NumSts4: gridRow.NumSts4, NumSts5: gridRow.NumSts5, SumLitr: gridRow.SumLitr,
    // running balance computed by getPayrollDetail (หักเงินประกัน), plus its ÷500 unit count
    // shown after the top-right label (e.g. หักเงินประกันสะสม(5))
    AccuDeSecDepAmnt: payroll.AccuDeSecDepAmnt || 0,
    AccuSecDepCnt: Math.round((payroll.AccuDeSecDepAmnt || 0) / 500 * 100) / 100,
  }
}

// the ข้อมูลสรุป box rows (trip counts + fuel), shared by both renderers
export const slipDetailRows = (data) => [
  { isDetail: true, label: "เที่ยว รับ", unit: "เที่ยว", value: data.NumSts1 },
  { isDetail: true, label: "เที่ยว ส่ง", unit: "เที่ยว", value: data.NumSts3 },
  { isDetail: true, label: "เที่ยว พัก", unit: "เที่ยว", value: data.NumSts4 },
  { isDetail: true, label: "เที่ยว คืน", unit: "เที่ยว", value: data.NumSts5 },
  { isDetail: true, label: "ใช้น้ำมัน", unit: "ลิตร", value: data.SumLitr },
]

// the สรุปยอด box rows, shared by both renderers. เบิกเบี้ยขยัน is only listed when there is
// one — this branch has no ExpId-78 advance, so the row would otherwise always read 0.
export const slipTotalRows = (data) => [
  { label: "รวมรายได้", amnt: data.SumInc },
  { label: "รวมรายการหัก", amnt: data.SumDeduct, minus: true },
  ...(data.advAttn ? [{ label: "เบิกเบี้ยขยัน", amnt: data.advAttn, minus: true }] : []),
  { label: "เบิกเบี้ยเลี้ยง", amnt: data.advTrip, minus: true },
]
